/**
 * All-defaults INSERT emit, per dialect.
 *
 * "No insertable columns left" is a real shape in two places: the Object
 * Explorer's `Script table as → INSERT` template for a table whose only column
 * is IDENTITY, and the results-grid INSERT builder when "Skip identity columns"
 * is on and every projected column is IDENTITY. Both used to emit
 * `INSERT INTO t () VALUES ();`, which only MySQL accepts.
 *
 * The per-engine forms mirror the existing precedents in
 * `electron/table-explorer/changeGenerator/*` — that layer already had to solve
 * "insert a row using nothing but defaults" and its choices are the ones proven
 * against live databases.
 */

type DefaultValuesInsertArgs = {
  /** Engine id. Accepts both `pg` (grid/query-engine) and `pgsql` (providers). */
  engineId?: string;
  /** Already-quoted, fully-qualified table name. */
  tableName: string;
  /**
   * Already-quoted column name. Required for Oracle, which has no
   * `DEFAULT VALUES` syntax and needs `(col) VALUES (DEFAULT)` instead.
   */
  quotedColumn?: string;
};

export function defaultValuesInsert({ engineId, tableName, quotedColumn }: DefaultValuesInsertArgs): string {
  if (engineId === 'mysql') {
    // The one dialect where an empty column list is legal.
    return `INSERT INTO ${tableName} () VALUES ();`;
  }

  if (engineId === 'oracle') {
    // Oracle has no DEFAULT VALUES; name one column and hand it DEFAULT.
    if (quotedColumn) return `INSERT INTO ${tableName} (${quotedColumn}) VALUES (DEFAULT);`;
    return `INSERT INTO ${tableName} VALUES (DEFAULT);`;
  }

  // mssql / pgsql / sqlite — and the safest default for anything unrecognized.
  return `INSERT INTO ${tableName} DEFAULT VALUES;`;
}
